import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ActionDescriptor } from '@finance/contracts';
import type { TableActionContext } from './TableRenderer';
import { GenericResourceListPage } from './GenericResourceListPage';
import { useDelegationEntry } from '../../features/auth/useDelegationEntry';
import { useUiStore } from '../../state/ui';

const ENTER_DELEGATION_ACTION_ID = 'action.platformSupportDelegation.enter';

/**
 * Adds browser-owned session rotation to the otherwise generic support-
 * delegation resource. Revoke and other commands continue through the generic
 * command/confirmation path.
 */
export function PlatformSupportDelegationListPage() {
  const navigate = useNavigate();
  const enterDelegation = useDelegationEntry();
  const { addToast } = useUiStore();

  const handleLocalAction = useCallback(async (
    action: ActionDescriptor,
    row: Record<string, unknown>,
    _context?: TableActionContext,
  ) => {
    if (action.id !== ENTER_DELEGATION_ACTION_ID) return;
    const delegationId = row.id;
    if (typeof delegationId !== 'string' || delegationId.length === 0) {
      addToast({ type: 'error', message: 'Delegation identifier is missing.' });
      return;
    }

    try {
      await enterDelegation(delegationId);
      addToast({ type: 'success', message: 'Delegated organization context activated.' });
      navigate('/admin', { replace: true });
    } catch {
      addToast({ type: 'error', message: 'Unable to enter the delegated organization context.' });
    }
  }, [addToast, enterDelegation, navigate]);

  return (
    <GenericResourceListPage
      resourceId="platform-support-delegations"
      onLocalAction={handleLocalAction}
    />
  );
}
